import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import api, { fileToBase64 } from "@/lib/api";
import { Switch } from "@/components/ui/switch";

const emptyProduct = {
  name: "",
  description: "",
  price: "",
  photo: "",
  photos: [],
  category: "",
  published: true,
  order: 0,
  stock: "",
  alert_threshold: "",
  featured: false,
  is_new: false,
};

export default function ProductsManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/products").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...editing,
        price: parseFloat(String(editing.price).replace(",", ".")) || 0,
        order: parseInt(editing.order, 10) || 0,
        stock:
          editing.stock === "" || editing.stock === null || editing.stock === undefined
            ? null
            : Math.max(0, parseInt(editing.stock, 10) || 0),
        alert_threshold:
          editing.alert_threshold === "" ||
          editing.alert_threshold === null ||
          editing.alert_threshold === undefined
            ? null
            : Math.max(0, parseInt(editing.alert_threshold, 10) || 0),
      };
      if (editing.id) {
        await api.put(`/admin/products/${editing.id}`, payload);
      } else {
        await api.post("/admin/products", payload);
      }
      toast.success("Produit enregistré.");
      setEditing(null);
      load();
    } catch {
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/products/${id}`);
    toast.success("Produit supprimé.");
    load();
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const base64 = await fileToBase64(file);
    setEditing((s) => ({ ...s, photo: base64 }));
  };

  const set = (key) => (e) => setEditing((s) => ({ ...s, [key]: e.target.value }));

  return (
    <div data-testid="products-manager">
      <div className="mb-8 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-ivory">Le Comptoir — {items.length} produit(s)</h2>
        <button onClick={() => setEditing({ ...emptyProduct })} className="btn-primary !h-10 !px-5 !text-[11px]" data-testid="product-add-button">
          <Plus className="h-4 w-4" strokeWidth={1.5} />
          Ajouter un produit
        </button>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="product-edit-modal">
          <form onSubmit={save} className="max-h-[90vh] w-full max-w-xl space-y-4 overflow-y-auto rounded-lg border border-white/10 bg-coal p-8">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-ivory">
                {editing.id ? "Modifier le produit" : "Nouveau produit"}
              </h3>
              <button type="button" onClick={() => setEditing(null)} aria-label="Fermer">
                <X className="h-5 w-5 text-ivory/50 hover:text-ivory" strokeWidth={1.5} />
              </button>
            </div>
            <input required value={editing.name} onChange={set("name")} placeholder="Nom (ex : Jus de bissap 1L)" className="input-dark !h-11" data-testid="product-name-input" />
            <textarea rows={2} value={editing.description} onChange={set("description")} placeholder="Description" className="input-dark !h-auto py-3" data-testid="product-description-input" />
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Prix (€)</label>
                <input required value={editing.price} onChange={set("price")} placeholder="6,50" className="input-dark !h-11" data-testid="product-price-input" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Catégorie</label>
                <input value={editing.category} onChange={set("category")} placeholder="Épicerie" className="input-dark !h-11" data-testid="product-category-input" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Ordre</label>
                <input type="number" value={editing.order} onChange={set("order")} className="input-dark !h-11" data-testid="product-order-input" />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">
                Stock (vide = illimité)
              </label>
              <input
                type="number"
                min="0"
                value={editing.stock ?? ""}
                onChange={set("stock")}
                placeholder="Illimité"
                className="input-dark !h-11"
                data-testid="product-stock-input"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">
                Seuil d'alerte stock (email automatique)
              </label>
              <input
                type="number"
                min="0"
                value={editing.alert_threshold ?? ""}
                onChange={set("alert_threshold")}
                placeholder="Ex : 3"
                className="input-dark !h-11"
                data-testid="product-threshold-input"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">
                Photos supplémentaires (carrousel)
              </label>
              <div className="flex flex-wrap items-center gap-3">
                {(editing.photos || []).map((p, i) => (
                  <div key={i} className="relative">
                    <img src={p} alt="" className="h-16 w-16 rounded-md border border-white/10 object-cover" />
                    <button
                      type="button"
                      onClick={() => setEditing((s) => ({ ...s, photos: s.photos.filter((_, j) => j !== i) }))}
                      aria-label="Retirer"
                      className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] text-white"
                      data-testid={`product-photo-remove-${i}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <label className="flex h-16 w-16 cursor-pointer items-center justify-center rounded-md border border-dashed border-white/20 text-ivory/40 transition-colors hover:border-copper hover:text-copper" data-testid="product-photos-upload">
                  +
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const b64 = await fileToBase64(file);
                      setEditing((s) => ({ ...s, photos: [...(s.photos || []), b64] }));
                    }}
                  />
                </label>
              </div>
            </div>
            <div>
              {editing.photo && (
                <img src={editing.photo} alt="" className="mb-3 h-20 w-20 rounded-md border border-white/10 object-cover" />
              )}
              <input type="file" accept="image/*" onChange={uploadPhoto} className="text-xs text-ivory/60 file:mr-3 file:rounded-md file:border-0 file:bg-white/10 file:px-3 file:py-2 file:text-xs file:text-ivory" data-testid="product-photo-upload" />
            </div>
            <label className="flex items-center gap-3 text-sm text-ivory/70">
              En vente
              <Switch checked={editing.published} onCheckedChange={(v) => setEditing((s) => ({ ...s, published: v }))} data-testid="product-published-switch" />
            </label>
            <div className="flex flex-wrap gap-8">
              <label className="flex items-center gap-3 text-sm text-ivory/70">
                Mis en avant
                <Switch checked={!!editing.featured} onCheckedChange={(v) => setEditing((s) => ({ ...s, featured: v }))} data-testid="product-featured-switch" />
              </label>
              <label className="flex items-center gap-3 text-sm text-ivory/70">
                Nouveauté
                <Switch checked={!!editing.is_new} onCheckedChange={(v) => setEditing((s) => ({ ...s, is_new: v }))} data-testid="product-new-switch" />
              </label>
            </div>
            <button type="submit" disabled={saving} className="btn-primary w-full" data-testid="product-save-button">
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </form>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-sm text-ivory/40" data-testid="products-empty">
          Aucun produit pour le moment. Ajoutez votre premier produit (bissap, épicerie, produits dérivés…).
        </p>
      ) : (
        <div className="space-y-2" data-testid="products-list">
          {items.map((p) => (
            <div key={p.id} className="flex items-center gap-4 rounded-lg border border-white/10 bg-coal/60 px-5 py-3.5" data-testid={`product-row-${p.id}`}>
              <span className="w-32 shrink-0 truncate text-xs uppercase tracking-wider text-gold/70">{p.category || "—"}</span>
              <span className={`flex-1 truncate text-sm ${p.published ? "text-ivory" : "text-ivory/35 line-through"}`}>
                {p.name}
                {p.featured && (
                  <span className="ml-2 rounded border border-gold/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-gold">
                    vedette
                  </span>
                )}
                {p.is_new && (
                  <span className="ml-2 rounded border border-copper/50 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-copper">
                    nouveau
                  </span>
                )}
              </span>
              <span className="text-sm text-gold">{String(p.price.toFixed(2)).replace(".", ",")} €</span>
              {p.stock !== null && p.stock !== undefined && (
                <span
                  className={`w-24 shrink-0 text-right text-xs ${p.stock <= 0 ? "font-semibold text-red-400" : "text-ivory/45"}`}
                  data-testid={`product-stock-${p.id}`}
                >
                  {p.stock <= 0 ? "Rupture" : `Stock : ${p.stock}`}
                </span>
              )}
              <button onClick={() => setEditing({ ...p })} aria-label="Modifier" className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/50 transition-colors hover:border-gold hover:text-gold" data-testid={`product-edit-${p.id}`}>
                <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
              </button>
              <button onClick={() => remove(p.id)} aria-label="Supprimer" className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/40 transition-colors hover:border-red-400/50 hover:text-red-400" data-testid={`product-delete-${p.id}`}>
                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
